/**
 * SubmitFieldAddDialog (PR5)
 * Compact dialog for ops/accounting to log a field-add against a job.
 * Auto-classifies tier preview (within-cap vs above-cap) based on the
 * contractor's pay profile, so the submitter knows whether accounting
 * approval will be required.
 */
import { useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { getPayProfile } from "@/data/contractorPayProfile";
import { createFieldAddSubmission } from "@/data/finance/payRun/fieldAdds";
import { toast } from "@/hooks/use-toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertCircle, ShieldCheck } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobLabel: string;
  partyId: string | null;
}

export default function SubmitFieldAddDialog({
  open, onOpenChange, jobId, jobLabel, partyId,
}: Props) {
  const { payProfiles, setFieldAddSubmissions } = useAppData();
  const { role } = useRole();
  const [amount, setAmount] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [busy, setBusy] = useState(false);

  const profile = partyId ? getPayProfile(payProfiles, partyId) : undefined;
  const cap = profile?.fieldAddCapAmount ?? 0;
  const amt = Number(amount);
  const validAmount = Number.isFinite(amt) && amt > 0;
  const overCap = validAmount && cap > 0 && amt > cap;
  const validReason = reason.trim().length >= 4;

  const reset = () => { setAmount(""); setReason(""); };

  const handleSubmit = () => {
    if (!validAmount || !validReason) return;
    setBusy(true);
    try {
      const rec = createFieldAddSubmission({
        jobId, partyId, amount: amt, reason, submittedBy: role,
      });
      setFieldAddSubmissions((prev) => [...prev, rec]);
      toast({
        title: "Field-add submitted",
        description: overCap
          ? `$${amt.toLocaleString()} · awaiting accounting approval`
          : `$${amt.toLocaleString()} · auto-approved within cap on release`,
      });
      reset();
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Submit failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Submit field-add</DialogTitle>
          <DialogDescription>{jobLabel}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="fa-amount" className="text-xs">Amount ($)</Label>
            <Input
              id="fa-amount" type="number" inputMode="decimal" min="0" step="0.01"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 250"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="fa-reason" className="text-xs">Reason</Label>
            <Textarea
              id="fa-reason" rows={3}
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why this field-add? (≥4 chars)"
              className="mt-1"
            />
          </div>

          {cap > 0 ? (
            <div className={`flex items-start gap-2 rounded-md border p-2 text-xs ${overCap ? "border-warning/40 bg-warning/5 text-warning-foreground" : "border-muted bg-muted/30 text-muted-foreground"}`}>
              {overCap ? <AlertCircle className="h-3.5 w-3.5 mt-0.5 text-warning" /> : <ShieldCheck className="h-3.5 w-3.5 mt-0.5" />}
              <div>
                <p className="font-medium">
                  {overCap ? "Above cap · accounting approval required" : "Within cap · auto-approved on release"}
                </p>
                <p>Field-add cap: ${cap.toLocaleString()}</p>
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              No contractor pay profile cap configured · accounting approval may be required.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!validAmount || !validReason || busy}>
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
